import { Skeleton } from '@mui/material';
import { memo } from 'react';

import { PILL_RADIUS_PX } from '../../constants';
import {
  SKELETON_CHIP_HEIGHT_PX,
  SKELETON_CHIP_WIDTH_PX,
} from './SuggestionChipSkeleton.styles';

export type SuggestionChipSkeletonProps = {
  /** Optional className appended to the root element. */
  className?: string;
};

/**
 * Pulse skeleton placeholder rendered in place of a `SuggestionChip`
 * while the upstream suggestions query is in flight. Dimensions and
 * border radius mirror the rendered chip exactly so the swap-in
 * introduces no layout shift.
 *
 * Pure presentational primitive — no Redux, no translation, no
 * flag gating. The skeleton is `aria-hidden` (the parent chip row
 * carries the loading semantic) and exposes a stable test-id for
 * unit + e2e assertions.
 *
 * Width and height are passed directly to `<Skeleton>` rather than
 * via className so MUI does not overwrite them with its default
 * `1.2em` height heuristic.
 */
function SuggestionChipSkeletonInner({
  className,
}: SuggestionChipSkeletonProps) {
  return (
    <Skeleton
      variant="rounded"
      animation="pulse"
      width={SKELETON_CHIP_WIDTH_PX}
      height={SKELETON_CHIP_HEIGHT_PX}
      sx={{ borderRadius: `${PILL_RADIUS_PX}px` }}
      className={className}
      aria-hidden
      data-testid="ai-sidekick-chip-skeleton"
    />
  );
}

const SuggestionChipSkeleton = memo(SuggestionChipSkeletonInner);
SuggestionChipSkeleton.displayName = 'SuggestionChipSkeleton';

export default SuggestionChipSkeleton;
